import { z } from 'zod';
import { timestampSchema, ulidSchema } from './common.js';

/**
 * Notification types. The table in `01-product-rules.md` section 8 says, per type, whether it
 * goes to the in-app feed and to an OS notification. `NOTIFICATION_ROUTING` below
 * is that table in code, and the notify queue reads it.
 */
export const NOTIFICATION_TYPES = [
  'build_available',
  'crew_invite_received',
  'crew_guest_joined',
  'crew_guest_left',
  'crew_removed',
  'crew_dissolved',
  'crew_ends_at_period_end',
  'membership_cancel_scheduled',
  'creator_approved',
  'game_purchased',
  'game_refunded',
] as const;
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export interface NotificationRouting {
  /** Written to the `notifications` table and shown in the feed. */
  feed: boolean;
  /** Shown as an OS notification by desktop and Android while the app runs. */
  os: boolean;
}

export const NOTIFICATION_ROUTING: Record<NotificationType, NotificationRouting> = {
  build_available: { feed: true, os: true },
  crew_invite_received: { feed: true, os: true },
  crew_guest_joined: { feed: true, os: true },
  crew_guest_left: { feed: true, os: true },
  crew_removed: { feed: true, os: true },
  crew_dissolved: { feed: true, os: true },
  crew_ends_at_period_end: { feed: true, os: true },
  membership_cancel_scheduled: { feed: true, os: false },
  creator_approved: { feed: true, os: false },
  // A purchase is worth an OS notification: somebody who just paid is waiting to be told it
  // worked, and the checkout leaves them on a page that may no longer be in front of them.
  game_purchased: { feed: true, os: true },
  game_refunded: { feed: true, os: false },
};

/** Types a user can silence in settings. Account and billing notices are not optional. */
export const MUTABLE_NOTIFICATION_TYPES = NOTIFICATION_TYPES.filter(
  (t) => NOTIFICATION_ROUTING[t].os,
);

export const notificationSchema = z.object({
  id: ulidSchema,
  type: notificationTypeSchema,
  title: z.string(),
  body: z.string(),
  /** In-app route, for example `/games/my-game`. */
  link: z.string().nullable(),
  createdAt: timestampSchema,
  read: z.boolean(),
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationFeedSchema = z.object({
  items: z.array(notificationSchema),
  cursor: z.string().nullable(),
  unreadCount: z.number().int(),
});
export type NotificationFeed = z.infer<typeof notificationFeedSchema>;
